import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api, type Tenant } from '../api';
import { Banner, Card, Copyable, Tabs } from '../components/ui';
import { SamlTab } from './SamlTab';
import { OidcTab } from './OidcTab';
import { PingTab } from './PingTab';
import { ResultsTab } from './ResultsTab';

type TabId = 'overview' | 'saml' | 'oidc' | 'ping' | 'results';

export function TenantDetailPage() {
  const { tenantId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as TabId) ?? 'overview';
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setTenant(null);
    api.get<Tenant>(`/api/tenants/${tenantId}`)
      .then(setTenant)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load tenant'));
  }, [tenantId]);

  if (error) return <Banner kind="error">{error}</Banner>;
  if (!tenant) return <p className="faint">Loading…</p>;

  return (
    <>
      <div className="row" style={{ marginBottom: 18 }}>
        <div>
          <h1 className="page-title">{tenant.name}</h1>
          <p className="page-sub">
            <code>{tenant.slug}</code>
            {tenant.description && <> — {tenant.description}</>}
          </p>
        </div>
        <div className="spacer" />
        <a className="badge proto" href={tenant.portalUrl} target="_blank" rel="noreferrer">
          Open tenant portal ↗
        </a>
      </div>

      <Tabs<TabId>
        active={tab}
        onChange={(t) => setParams({ tab: t })}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'saml', label: 'SAML SP' },
          { id: 'oidc', label: 'OAuth / OIDC' },
          { id: 'ping', label: 'PingAccess' },
          { id: 'results', label: 'Results' },
        ]}
      />

      {tab === 'overview' && <Overview tenant={tenant} onChange={setTenant} />}
      {tab === 'saml' && <SamlTab tenantId={tenantId} />}
      {tab === 'oidc' && <OidcTab tenantId={tenantId} />}
      {tab === 'ping' && <PingTab tenantId={tenantId} />}
      {tab === 'results' && <ResultsTab tenantId={tenantId} />}
    </>
  );
}

function Overview({ tenant, onChange }: { tenant: Tenant; onChange: (t: Tenant) => void }) {
  const [rotating, setRotating] = useState(false);
  const [msg, setMsg] = useState('');

  const rotate = async () => {
    if (!confirm(
      'Rotate this tenant’s SP signing key?\n\n' +
      'Every IdP that trusts the current certificate will reject signed AuthnRequests ' +
      'until you upload the new one. Existing assertions encrypted to the old key ' +
      'will no longer decrypt.',
    )) return;
    setRotating(true);
    try {
      const { certificate } = await api.post<{ certificate: string }>(`/api/tenants/${tenant.id}/rotate-keys`, {});
      onChange({ ...tenant, spCertificate: certificate });
      setMsg('New key generated. Upload the certificate below to every IdP that trusts this SP.');
    } finally {
      setRotating(false);
    }
  };

  return (
    <>
      {msg && <Banner kind="warning">{msg}</Banner>}

      <Card title="Endpoint namespace" hint="Everything for this tenant lives under this prefix.">
        <Copyable label="Tenant portal" value={tenant.portalUrl} />
        <Copyable label="Public base URL" value={tenant.publicBaseUrl} />
        <p className="hint" style={{ marginBottom: 0 }}>
          The base URL must be exactly what the IdP and PingAccess see from the outside —
          ACS URLs, redirect URIs and entity IDs are all derived from it. Behind a proxy,
          set <code>PUBLIC_BASE_URL</code> and <code>TRUST_PROXY=1</code>.
        </p>
      </Card>

      <Card
        title="Service provider signing key"
        hint="One keypair per tenant. Signs SAML AuthnRequests and decrypts EncryptedAssertions."
        actions={<button className="danger" onClick={rotate} disabled={rotating}>
          {rotating ? 'Rotating…' : 'Rotate key'}
        </button>}
      >
        <pre className="code mono" style={{ maxHeight: 220 }}>{tenant.spCertificate}</pre>
      </Card>

      <Card title="Where to go next">
        <ul className="stack" style={{ margin: 0, paddingLeft: 18 }}>
          <li>
            <Link to={`/admin/tenants/${tenant.id}?tab=saml`}>SAML SP</Link> — import IdP metadata,
            then hand the IdP your ACS URL and SP metadata.
          </li>
          <li>
            <Link to={`/admin/tenants/${tenant.id}?tab=oidc`}>OAuth / OIDC</Link> — point at an issuer,
            register the redirect URI, run an authorization-code flow.
          </li>
          <li>
            <Link to={`/admin/tenants/${tenant.id}?tab=ping`}>PingAccess</Link> — put the app URL behind
            a PingAccess application and inspect the identity headers and mediated token it injects.
          </li>
        </ul>
      </Card>
    </>
  );
}
